// Simple Passkey Service without cross-platform support
import { doc, getDoc, updateDoc, collection, query, where, getDocs, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import deviceInfoService from './deviceInfo';

export interface PasskeyRegistrationOptions {
  email: string;
}

export interface RegisteredPasskey {
  credentialId: string;
  createdAt: string;
  lastUsedAt: string;
}

export class PasskeyService {
  // Add a flag to track ongoing authentication to prevent duplicate prompts
  private isAuthenticating = false;
  // Cache for challenge to avoid regenerating it multiple times
  private challengeCache: ArrayBuffer | null = null;
  // Cache timeout in milliseconds
  private readonly CHALLENGE_CACHE_TIMEOUT = 30000; // 30 seconds

  async isSupported(): Promise<boolean> {
    return window.PublicKeyCredential !== undefined;
  }

  private async generateChallenge(): Promise<ArrayBuffer> {
    // Return cached challenge if available
    if (this.challengeCache) {
      return this.challengeCache;
    }
    
    const challenge = new Uint8Array(32);
    crypto.getRandomValues(challenge);
    this.challengeCache = challenge.buffer;
    
    // Clear the cache after timeout
    setTimeout(() => {
      this.challengeCache = null;
    }, this.CHALLENGE_CACHE_TIMEOUT);
    
    return this.challengeCache;
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const base64 = btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '')
      .replace(/-+$/, ''); // Remove any trailing dashes
    return base64;
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    // Add padding if needed
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const b64 = (base64 + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    try {
      const binaryStr = atob(b64);
      const bytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) {
        bytes[i] = binaryStr.charCodeAt(i);
      }
      return bytes.buffer;
    } catch (error) {
      console.error('Error decoding base64:', error);
      console.error('Original base64:', base64);
      console.error('Processed base64:', b64);
      throw error;
    }
  }

  // Enhanced register method with device name and authenticator options
  async register(options: PasskeyRegistrationOptions): Promise<RegisteredPasskey> {
    if (!await this.isSupported()) {
      throw new Error('WebAuthn is not supported in this browser');
    }

    // Prevent multiple simultaneous registrations
    if (this.isAuthenticating) {
      throw new Error('Authentication or registration already in progress');
    }

    this.isAuthenticating = true;

    try {
      console.log('Starting passkey registration for:', options.email);
      const challenge = await this.generateChallenge();
      
      const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
        challenge,
        rp: {
          name: 'Strategiz',
          id: window.location.hostname
        },
        user: {
          id: crypto.getRandomValues(new Uint8Array(16)),
          name: options.email,
          displayName: options.email.split('@')[0]
        },
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },
          { type: 'public-key', alg: -257 }
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'preferred',
          residentKey: 'preferred'
        },
        timeout: 60000,
        attestation: 'none'
      };

      console.log('Requesting credential creation with options:', {
        ...publicKeyCredentialCreationOptions,
        challenge: 'ArrayBuffer',
        user: {
          ...publicKeyCredentialCreationOptions.user,
          id: 'Uint8Array'
        }
      });

      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions
      });

      if (!credential) {
        console.error('No credential returned from create()');
        throw new Error('Failed to create credential');
      }

      const credentialId = this.arrayBufferToBase64((credential as PublicKeyCredential).rawId);
      console.log('Successfully generated credential ID:', credentialId);
      
      const registeredPasskey: RegisteredPasskey = {
        credentialId,
        createdAt: new Date().toISOString(),
        lastUsedAt: new Date().toISOString()
      };

      // Store credential ID and email mapping in a separate collection
      const credentialRef = doc(db, 'credentials', credentialId);
      await setDoc(credentialRef, {
        userId: options.email,
        credentialId,
        createdAt: registeredPasskey.createdAt
      });

      console.log('Created RegisteredPasskey:', registeredPasskey);
      return registeredPasskey;
    } catch (error) {
      console.error('Error in passkey registration:', error);
      if (error instanceof Error) {
        console.error('Error details:', error.message);
        console.error('Error stack:', error.stack);
      }
      throw new Error(error instanceof Error ? error.message : 'Failed to register passkey');
    } finally {
      // Reset the authentication flag
      this.isAuthenticating = false;
    }
  }

  // List registered passkeys for a user
  async listPasskeys(userId: string): Promise<RegisteredPasskey[]> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();
      return userData.authMethods?.passkey?.credentials || [];
    } catch (error) {
      console.error('Error listing passkeys:', error);
      throw new Error('Failed to list passkeys');
    }
  }

  // Remove a registered passkey
  async removePasskey(userId: string, credentialId: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();
      const credentials = userData.authMethods?.passkey?.credentials || [];
      
      // Remove the specified credential
      const updatedCredentials = credentials.filter(
        (cred: RegisteredPasskey) => cred.credentialId !== credentialId
      );

      await updateDoc(userRef, {
        'authMethods.passkey.credentials': updatedCredentials
      });
    } catch (error) {
      console.error('Error removing passkey:', error);
      throw new Error('Failed to remove passkey');
    }
  }

  // Get available passkeys for the user
  async discoverPasskeys(): Promise<PublicKeyCredentialDescriptor[]> {
    if (!await this.isSupported()) {
      throw new Error('WebAuthn is not supported in this browser');
    }

    try {
      const challenge = await this.generateChallenge();
      
      // Create a minimal options object for discovery
      const options: PublicKeyCredentialRequestOptions = {
        challenge,
        rpId: window.location.hostname,
        userVerification: 'preferred',
        timeout: 60000
      };

      // This will trigger the browser to show available passkeys
      const credential = await navigator.credentials.get({
        publicKey: options
      }) as PublicKeyCredential;

      if (!credential) {
        return [];
      }

      // Return the credential descriptor
      return [{
        id: credential.rawId,
        type: 'public-key',
        transports: (credential.response as AuthenticatorAssertionResponse).authenticatorData ? ['internal'] : undefined
      }];
    } catch (error) {
      console.error('Error discovering passkeys:', error);
      throw new Error('Failed to discover passkeys');
    }
  }

  // Modified authenticate method that doesn't require a specific credential ID
  async authenticate(credentialId?: string): Promise<{ success: boolean; email?: string; provider?: string }> {
    if (!await this.isSupported()) {
      throw new Error('WebAuthn is not supported in this browser');
    }

    // Prevent multiple simultaneous authentications
    if (this.isAuthenticating) {
      console.warn('Authentication already in progress, returning cached result');
      return { success: false, provider: 'passkey' };
    }

    this.isAuthenticating = true;

    try {
      const challenge = await this.generateChallenge();
      console.log('Starting authentication with challenge');

      const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
        challenge,
        rpId: window.location.hostname,
        userVerification: 'preferred',
        timeout: 60000
      };

      if (credentialId) {
        console.log('Using provided credentialId:', credentialId);
        publicKeyCredentialRequestOptions.allowCredentials = [{
          id: this.base64ToArrayBuffer(credentialId.replace(/-+$/, '').trim()),
          type: 'public-key'
        }];
      }

      console.log('Requesting credential with options:', {
        ...publicKeyCredentialRequestOptions,
        challenge: 'ArrayBuffer'
      });

      const assertion = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions
      }) as PublicKeyCredential;

      if (!assertion) {
        console.error('No assertion returned');
        return { success: false };
      }

      const assertedCredentialId = this.arrayBufferToBase64(assertion.rawId);
      console.log('Received credential ID:', assertedCredentialId);

      // Look up the credential in our credentials collection
      const credentialRef = doc(db, 'credentials', assertedCredentialId);
      const credentialDoc = await getDoc(credentialRef);

      if (!credentialDoc.exists()) {
        console.error('No credential found in credentials collection');
        return { success: false };
      }

      const credentialData = credentialDoc.data();
      const userId = credentialData.userId;

      // Get the user document
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        console.error('User document not found');
        return { success: false };
      }

      const userData = userDoc.data();
      console.log('Found user:', userId);

      // Update the user's last login
      await updateDoc(userRef, {
        'lastLoginAt': new Date().toISOString(),
        'authMethods.passkey.lastVerifiedAt': new Date().toISOString()
      });

      // Register this device if it's not already registered - do this asynchronously
      // to avoid blocking the authentication flow
      setTimeout(async () => {
        try {
          await deviceInfoService.registerDeviceForUser(userId, 'passkey');
        } catch (error) {
          console.warn('Failed to register device:', error);
        }
      }, 0);

      return {
        success: true,
        email: userId,
        provider: 'passkey'
      };
    } catch (error) {
      console.error('Error in authentication:', error);
      return { success: false };
    } finally {
      // Reset the authentication flag with a small delay to prevent immediate re-triggering
      setTimeout(() => {
        this.isAuthenticating = false;
      }, 500);
    }
  }

  // SMS Verification Methods
  async sendVerificationCode(phoneNumber: string): Promise<string> {
    try {
      // Initialize reCAPTCHA if not already done
      const recaptchaContainer = document.getElementById('recaptcha-container');
      if (!recaptchaContainer) {
        throw new Error('reCAPTCHA container not found');
      }

      // TODO: Replace with actual SMS verification implementation
      // This is a mock implementation
      const verificationId = Math.random().toString(36).substring(2, 15);
      console.log('Mock SMS sent to:', phoneNumber);
      return verificationId;
    } catch (error) {
      console.error('Error sending SMS:', error);
      throw new Error('Failed to send verification code');
    }
  }

  async verifySMSCode(phoneNumber: string, code: string): Promise<boolean> {
    try {
      // TODO: Replace with actual SMS verification implementation
      // This is a mock implementation
      console.log('Verifying code for:', phoneNumber);
      // For testing, accept any 6-digit code
      return /^\d{6}$/.test(code);
    } catch (error) {
      console.error('Error verifying SMS code:', error);
      throw new Error('Failed to verify SMS code');
    }
  }
}

export const passkeyService = new PasskeyService();
